import { useTranslation } from "react-i18next";

import { PAGE_SIZE } from "@/api/queries";

/**
 * Page-number pagination, matching DRF's. Deliberately not infinite scroll: a
 * seller working a list needs to be able to say "I got to page 4", leave, and
 * come back — and the page number is in the URL, so they can.
 */
export function Pagination({
  page,
  count,
  shown,
  onChange,
  isFetching,
  pageSize = PAGE_SIZE,
}: {
  page: number;
  /** Total matching rows, from DRF's `count`. */
  count: number;
  /** How many rows this page actually rendered. */
  shown: number;
  onChange: (page: number) => void;
  isFetching: boolean;
  /** Rows per page, when the list asked the API for fewer than the default. */
  pageSize?: number;
}) {
  const { t } = useTranslation();
  const lastPage = Math.max(1, Math.ceil(count / pageSize));
  const first = shown === 0 ? 0 : (page - 1) * pageSize + 1;
  // From the rows in hand, not from `page * PAGE_SIZE`: the last page is
  // shorter than a full one, and computing the range from the page number
  // claims rows that were never rendered.
  const last = first === 0 ? 0 : first + shown - 1;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-rule px-4 py-3 text-sm">
      <p className="text-mute-600" aria-live="polite">
        {/* The range is its own element so it stays one text node — otherwise
            the suffix below splits it and it can no longer be read, or queried,
            as a single string. */}
        <span>
          {shown === 0
            ? t("pagination.empty")
            : t("pagination.range", {
                first,
                last,
                total: count.toLocaleString("es-MX"),
              })}
        </span>
        {isFetching ? <span>{t("pagination.updating")}</span> : null}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="btn btn-secondary px-3 py-1"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
        >
          {t("pagination.previous")}
        </button>
        <span className="tabular-nums text-mute-600">
          {page} / {lastPage}
        </span>
        <button
          type="button"
          className="btn btn-secondary px-3 py-1"
          disabled={page >= lastPage}
          onClick={() => onChange(page + 1)}
        >
          {t("pagination.next")}
        </button>
      </div>
    </div>
  );
}
